/**
 * One 13-cell lane per resource group: which groups are the problem, before you commit to
 * opening one.
 *
 * Three states, and the third is the one that matters. Hatched = still behind. Solid teal =
 * full but current. Soft teal = comfortable, i.e. there is room here. "At capacity" and
 * "behind" are not the same answer to "can we take this on", and a percentage grid cannot
 * tell them apart at a glance.
 *
 * The lanes are LEVELLED in both views. Whether a group clears is a fact about the estate,
 * not about which encoding you are currently reading the charts in.
 *
 * AT GROUP, THE LANES SIT IN ENTITY BANDS (20 Aug 2026, Mike). The entity is said once in a
 * band header instead of thirteen times in the label column, which buys the room back for
 * the names, and the header carries what no single row can: how many groups the entity has,
 * what they hold per week, and how many of them never clear. "Kirkstall has a 3-of-6
 * problem" is the finding, and it now has somewhere to live.
 *
 * The cost is real and worth stating: the worst lanes are no longer adjacent. Oracle's four
 * that never clear sit below Kirkstall's clearing ones, because the band is by estate and
 * the ordering within it is by load.
 */

import React from 'react'
import type { ResourceModel } from '../../viewmodels/loading'
import type { Site } from '../../types/metrics'
import { ageRampLabel, hrs, laneCell, tickEvery, weekLabel, weekMonth } from './format'

const GRID = 'grid grid-cols-[164px_58px_1fr_118px] items-center gap-x-3'

/**
 * The age column. It shows the HOURS-WEIGHTED MEAN, not the oldest (G38, 20 Aug 2026).
 *
 * It used to show the oldest, which is a minimum over the vintages: one small forgotten
 * operation sets a lane's headline as firmly as a thousand hours would. Two Kirkstall works
 * orders carrying 84.1h between them — 0.26% of the served book — set the age of 13 of the 24
 * lanes, and STAR read "200 wks" on 4.7h of 2022 work sitting behind 1,675h of recent arrears.
 *
 * The oldest is still true and still worth knowing, so it goes in the tooltip rather than
 * away: "how late is this queue" and "is something ancient stuck in here" are two questions
 * and the lane has room to lead with one.
 */
function ageTitle(r: ResourceModel): string | undefined {
  if (r.meanArrears === null && r.oldestArrears === null) return undefined
  const parts = []
  if (r.meanArrears !== null) parts.push(`arrears average ${Math.round(r.meanArrears)} weeks late`)
  if (r.oldestArrears !== null) parts.push(`oldest ${Math.round(r.oldestArrears)} weeks`)
  return parts.join(' · ')
}

/** How late this queue is: the mean, with the oldest on hover. See ageTitle. */
function Age({ r }: { r: ResourceModel }): React.JSX.Element {
  return (
    <span
      className="whitespace-nowrap text-right font-mono text-[10px]"
      style={{ color: 'var(--lc-mut)' }}
      title={ageTitle(r)}
    >
      {r.meanArrears === null ? '—' : `${Math.round(r.meanArrears)} wks`}
    </span>
  )
}

/** The 13 cells. Behind = red at the depth its age earns; teal = clear. See format.laneCell. */
function Cells({ weeks }: { weeks: ResourceModel['weeks'] }): React.JSX.Element {
  return (
    <span className="flex h-[18px] items-stretch gap-[3px]">
      {weeks.map((w) => {
        const cell = laneCell(w)
        return (
          <span
            key={w.weekStart}
            className={`flex-1 rounded-[2px] ${cell.className ?? ''}`}
            style={{ background: cell.background }}
          />
        )
      })}
    </span>
  )
}

function Lane({ r, onOpen }: { r: ResourceModel; onOpen: () => void }): React.JSX.Element {
  const clears = r.clearIdx >= 0
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`lc-lane -mx-2 w-[calc(100%+1rem)] rounded-md border-b px-2 py-[5px] text-left ${GRID}`}
      style={{ borderColor: 'var(--lc-b05)' }}
    >
      <span className="flex min-w-0 items-baseline gap-1.5">
        <span className="font-sans text-[11.5px] font-medium" style={{ color: 'var(--lc-ink)' }}>{r.name}</span>
        <span className="whitespace-nowrap font-mono text-[10px]" style={{ color: 'var(--lc-faint)' }}>
          {hrs(r.capacity)}/wk
        </span>
      </span>
      <Age r={r} />
      <Cells weeks={r.weeks} />
      <span
        className="text-right font-mono text-[10px] font-semibold uppercase tracking-[.04em]"
        style={{ color: clears ? 'var(--lc-teal)' : 'var(--lc-red)' }}
      >
        {clears ? `clears ${weekLabel(r.weeks[r.clearIdx].weekStart)}` : 'never clears'}
      </span>
    </button>
  )
}

/** The weeks the lanes are drawn on, named once at the top: the month where it changes,
 *  and the week's number under it. A lane says "clears 26 Oct"; the ruler is what lets you
 *  see where that sits without reading the date.
 *
 *  The NUMBER row thins on a long horizon (see format.tickEvery). The MONTH row does not,
 *  and must not: it marks a real boundary, and thinning it would move the boundary. It has
 *  the room because a month name is only printed where the month changes, so the cells it
 *  spills into are blank by construction. */
function Ruler({ weeks }: { weeks: ResourceModel['weeks'] }): React.JSX.Element {
  const every = tickEvery(weeks.length)
  return (
    <div className={`px-2 pb-1.5 ${GRID}`} aria-hidden>
      <span />
      {/* `oldest` until 20 Aug 2026, when the column stopped showing the oldest (G38). A
          caption that names the wrong statistic is worse than none: the number under it went
          from 200 to 45 for the same lane and nothing on screen would have said why. */}
      <span className="text-right font-mono text-[9px]" style={{ color: 'var(--lc-faint)' }}>avg late</span>
      <span className="flex items-stretch gap-[3px]">
        {weeks.map((w, i) => (
          <span key={w.weekStart} className="flex-1 font-mono text-[9px] leading-[1.5]">
            <span className="block" style={{ color: 'var(--lc-mut)' }}>
              {i === 0 || weekMonth(w.weekStart) !== weekMonth(weeks[i - 1].weekStart)
                ? weekMonth(w.weekStart)
                : '\u00a0'}
            </span>
            <span className="block" style={{ color: 'var(--lc-faint)' }}>
              {i % every === 0 ? i + 1 : '\u00a0'}
            </span>
          </span>
        ))}
      </span>
      <span />
    </div>
  )
}

/** What no single lane can say: the size of the estate and how much of it never clears. */
function BandHead({ site, groups }: { site: Site; groups: ResourceModel[] }): React.JSX.Element {
  const capacity = groups.reduce((total, r) => total + r.capacity, 0)
  const stuck = groups.filter((r) => r.clearIdx < 0).length
  return (
    <div className={`border-b px-2 pb-[5px] pt-3.5 ${GRID}`} style={{ borderColor: 'var(--lc-b09)' }}>
      <span className="flex min-w-0 items-baseline gap-2">
        <span
          aria-hidden
          className="inline-block h-[11px] w-[3px] rounded-[2px]"
          style={{ background: 'var(--lc-blue)' }}
        />
        <span
          className="font-mono text-[10.5px] font-semibold uppercase tracking-[.08em]"
          style={{ color: 'var(--lc-ink)' }}
        >
          {site}
        </span>
      </span>
      <span />
      <span className="font-mono text-[10px]" style={{ color: 'var(--lc-faint)' }}>
        {groups.length} groups · {hrs(capacity)}/wk
      </span>
      <span
        className="whitespace-nowrap text-right font-mono text-[10px] font-semibold"
        style={{ color: stuck ? 'var(--lc-red)' : 'var(--lc-teal)' }}
      >
        {stuck} of {groups.length} never clear
      </span>
    </div>
  )
}

/** Bands in the order the resources already arrive in — Kirkstall's groups, then Oracle's
 *  (viewmodels/loading byEntityThenLoad), so the band is a grouping of that order and never
 *  a re-sort of it. */
function bandsOf(resources: ResourceModel[]): { site: Site; groups: ResourceModel[] }[] {
  const bands: { site: Site; groups: ResourceModel[] }[] = []
  for (const r of resources) {
    const last = bands[bands.length - 1]
    if (last && last.site === r.site) last.groups.push(r)
    else bands.push({ site: r.site, groups: [r] })
  }
  return bands
}

/** The section header: what this is, and how to read the red under it. */
function Header({ scopeName }: { scopeName: string }): React.JSX.Element {
  return (
      <div className="flex items-baseline justify-between gap-3 pb-2.5">
      <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[.09em]" style={{ color: 'var(--lc-mut)' }}>
        Clearance by resource group · {scopeName}
      </span>
      <span className="flex items-center gap-3 font-mono text-[9.5px]" style={{ color: 'var(--lc-faint)' }}>
        <span className="flex items-center gap-1.5">
          arrears age
          <span
            aria-hidden
            className="inline-block h-[9px] w-[86px] rounded-[2px]"
            style={{
              background:
                'linear-gradient(90deg,' +
                ' color-mix(in srgb, var(--lc-hatch-a) 90%, transparent),' +
                ' color-mix(in srgb, var(--lc-hatch-a) 22%, transparent))',
            }}
          />
          {/* DERIVED, not prose (20 Aug 2026). It read "4 yrs → 1 wk" — hardcoded, two inches
              from the swatch it describes, and stale the moment the ramp moved. See
              format.ageRampLabel. */}
          {ageRampLabel()}
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="inline-block h-[9px] w-[14px] rounded-[2px]" style={{ background: 'var(--lc-teal)' }} />
          clear
        </span>
      </span>
    </div>
  )
}

export default function ClearanceLanes({ resources, scopeName, banded, onOpen }: {
  resources: ResourceModel[]
  scopeName: string
  /** Group scope: one band per entity. A single entity is its own band and needs no header. */
  banded: boolean
  onOpen: (id: string) => void
}): React.JSX.Element {
  return (
    <div className="px-[22px] pt-[18px]">
      <Header scopeName={scopeName} />
      {resources.length > 0 && <Ruler weeks={resources[0].weeks} />}
      {banded
        ? bandsOf(resources).map((band) => (
            <React.Fragment key={band.site}>
              <BandHead site={band.site} groups={band.groups} />
              {band.groups.map((r) => (
                <Lane key={r.id} r={r} onOpen={() => onOpen(r.id)} />
              ))}
            </React.Fragment>
          ))
        : resources.map((r) => <Lane key={r.id} r={r} onOpen={() => onOpen(r.id)} />)}
    </div>
  )
}
